import { useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
  {
    q: "How do I book a vehicle?",
    a: "Pick your dates on the booking bar or the Fleet page, choose your vehicle, and check out online in minutes. Your reservation is confirmed instantly and you receive an email and SMS confirmation with your booking reference.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit and debit cards (Visa, Mastercard, AMEX) through our secure checkout, plus PayPal and Zelle. Full pricing is shown upfront before you pay — no hidden fees.",
  },
  {
    q: "Is there a security deposit?",
    a: "Yes — a refundable $150 deposit hold is added to your total at checkout. It is released in full once the vehicle is returned on time, undamaged, and with the same fuel level.",
  },
  {
    q: "What documents do I need to rent?",
    a: "International visitors need a valid passport and driver's license from their home country. Local renters need a valid Bahamian driver's license. You can upload your documents during express check-in before you arrive.",
  },
  {
    q: "I'm a Nassau local — can I rent with my Bahamian license?",
    a: "Absolutely — we rent to locals and visitors alike at the same transparent rates. Local renters just need a valid Bahamian driver's license (no passport required), and local rentals have a 2-day minimum. Perfect for weekend errands, airport runs, or while your own car is in the shop.",
  },
  {
    q: "How old do I need to be to rent?",
    a: "All drivers must be 25 years or older with a valid driver's license. Additional drivers can be added to your booking for $25 each.",
  },
  {
    q: "Do you offer airport pickup and drop-off?",
    a: "We offer airport drop-off for a flat $25 fee, automatically added to your invoice — just leave the car with us at NAS when you fly out. For pickups, we deliver free to your hotel, villa, the cruise port, or our office on West Bay Street (airport pickups are not available at this time).",
  },
  {
    q: "Can I add extras to my rental?",
    a: "Absolutely. Add a baby/child seat ($7/day — free on rentals of 12 days or more), a GPS navigation unit with island maps preloaded ($5/day), additional drivers ($25 each), and a Paradise Island bridge pass ($8.99 service fee + $3 per trip over the bridge) right at checkout.",
  },
  {
    q: "What is the fuel policy?",
    a: "The vehicle is provided with a noted fuel level and must be returned at the same level. A photo of the fuel gauge is required at return to confirm.",
  },
  {
    q: "What is the cancellation policy?",
    a: "Cancellations made more than 24 hours before pickup are refunded less a 20% cancellation charge. Cancellations within 24 hours of pickup are non-refundable. Adding an unauthorized driver voids your rental without refund.",
  },
  {
    q: "Can I extend my rental?",
    a: "Yes — you can extend directly from your account page or invoice before your scheduled return time. Extensions must be made before your time is up, otherwise a late fee of $25/hour applies.",
  },
  {
    q: "Do you have a loyalty program?",
    a: "Yes — new renters pay regular price for their first 2 rentals, then discounts apply automatically and stay separate from coupon codes: Island Guest (2% off at 3–6 rentals), Silver Explorer (5% off at 7+ rentals), Gold Voyager (10% off at 20+), and Platinum Islander (15% off at 33+). Platinum Islander members also get a free GPS unit and free baby car seat (subject to availability). Silver Explorer members enjoy early pickup reservations (subject to availability). Gold Voyager members enjoy half-price GPS navigation unit and baby car seat (subject to availability), 1 hour of free late return, early pickup reservations, half-price additional drivers and complimentary vehicle upgrades. Platinum Islander members enjoy 2 hours of free late return, early pickups at no extra charge, free additional drivers and complimentary vehicle upgrades (subject to availability).",
  },
  {
    q: "How do I contact you?",
    a: "Message us on WhatsApp at +1 (242) 813-1775, email bookings@easydrivecarrental.com, or use the contact form on our Contact page. We're happy to help with any questions before or during your rental.",
  },
];

export default function FAQ() {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "faq-jsonld";
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);
    return () => document.getElementById("faq-jsonld")?.remove();
  }, []);

  return (
    <section data-testid="home-faq" className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary text-center">Good to know</p>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-center">
        Frequently asked <span className="italic gold-text">questions</span>
      </h2>
      <Accordion type="single" collapsible className="mt-10 space-y-3">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="bg-white border border-border rounded-2xl px-6 transition-colors duration-200 hover:border-primary/40">
            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg py-5 hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent data-testid={`faq-content-${i}`} className="text-sm text-muted-foreground font-ui leading-relaxed pb-5">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
